import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { Category } from '@rosita/shared';
import { api, extractError } from '../../../lib/api';
import { useToast } from '../../../components/admin/Toast';
import { dangerBtnText, inputClass, primaryBtn, secondaryBtn, slugify } from './shared';

interface EditDraft {
  name: string;
  slug: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
}

export default function CategoriesTab(): JSX.Element {
  const toast = useToast();
  const [items, setItems] = useState<Category[]>([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [parentId, setParentId] = useState<string>('');
  const [sortOrder, setSortOrder] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);

  const ensureUniqueSlug = (base: string, ignoreId?: string): string => {
    if (!base) return '';
    const taken = new Set(
      items.filter((c) => c.id !== ignoreId).map((c) => c.slug)
    );
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base}-${n}`)) n += 1;
    return `${base}-${n}`;
  };

  const slugCollision =
    slug !== '' && items.some((c) => c.slug === slug);

  const load = async (): Promise<void> => {
    const response = await api.get<Category[]>('/admin/categories');
    setItems(response.data);
  };

  useEffect(() => {
    void load();
  }, []);

  const topLevel = useMemo(() => items.filter((c) => !c.parentId), [items]);
  const childrenByParent = useMemo(() => {
    const map = new Map<string, Category[]>();
    for (const c of items) {
      if (c.parentId) {
        const arr = map.get(c.parentId) ?? [];
        arr.push(c);
        map.set(c.parentId, arr);
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [items]);

  const handleCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await api.post('/admin/categories', {
        name,
        slug,
        parentId: parentId || null,
        sortOrder,
        isActive: true,
      });
      setName('');
      setSlug('');
      setSlugManuallyEdited(false);
      setParentId('');
      setSortOrder(0);
      toast.success('Categoria criada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao criar categoria'));
    }
  };

  const startEdit = (c: Category): void => {
    setEditingId(c.id);
    setDraft({
      name: c.name,
      slug: c.slug,
      parentId: c.parentId,
      sortOrder: c.sortOrder,
      isActive: c.isActive,
    });
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingId || !draft) return;
    try {
      await api.put(`/admin/categories/${editingId}`, {
        name: draft.name,
        slug: draft.slug,
        parentId: draft.parentId,
        sortOrder: draft.sortOrder,
        isActive: draft.isActive,
      });
      setEditingId(null);
      setDraft(null);
      toast.success('Categoria atualizada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao atualizar categoria'));
    }
  };

  const remove = async (c: Category): Promise<void> => {
    const children = childrenByParent.get(c.id) ?? [];
    const warning = children.length > 0
      ? `"${c.name}" tem ${children.length} sub-categoria(s). Apagar assim mesmo?`
      : `Apagar "${c.name}"?`;
    if (!window.confirm(warning)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success('Categoria eliminada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao apagar categoria'));
    }
  };

  const renderRow = (c: Category, depth: number): JSX.Element => {
    const isEditing = editingId === c.id;
    return (
      <li
        key={c.id}
        className="flex flex-wrap items-center gap-3 border-b border-admin-border p-3 last:border-b-0"
        style={{ paddingLeft: `${12 + depth * 24}px` }}
      >
        {isEditing && draft ? (
          <>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className={inputClass}
              placeholder="Nome"
            />
            <input
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              className={inputClass}
              placeholder="slug"
            />
            <select
              value={draft.parentId ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, parentId: e.target.value ? e.target.value : null })
              }
              className={inputClass}
            >
              <option value="">— Sem pai</option>
              {topLevel
                .filter((p) => p.id !== c.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
            <input
              type="number"
              value={draft.sortOrder}
              onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
              className={`${inputClass} w-20`}
              title="Ordem"
            />
            <label className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
              Ativa
            </label>
            <button type="button" onClick={() => void saveEdit()} className={primaryBtn}>
              Guardar
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setDraft(null);
              }}
              className={secondaryBtn}
            >
              Cancelar
            </button>
          </>
        ) : (
          <>
            <span className="flex-1">
              {c.name}{' '}
              <small className="text-admin-muted">({c.slug})</small>
              {!c.isActive ? (
                <span className="ml-2 rounded bg-admin-surface-alt px-2 py-0.5 text-xs text-admin-muted">
                  inativa
                </span>
              ) : null}
            </span>
            <button type="button" onClick={() => startEdit(c)} className={secondaryBtn}>
              Editar
            </button>
            <button
              type="button"
              onClick={() => {
                void remove(c);
              }}
              className={dangerBtnText}
            >
              Apagar
            </button>
          </>
        )}
      </li>
    );
  };

  return (
    <section>
      <form
        onSubmit={(e) => {
          void handleCreate(e);
        }}
        className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_100px_auto]"
      >
        <input
          placeholder="Nome"
          value={name}
          onChange={(e) => {
            const v = e.target.value;
            setName(v);
            if (!slugManuallyEdited) {
              setSlug(ensureUniqueSlug(slugify(v)));
            }
          }}
          required
          className={inputClass}
        />
        <input
          placeholder="slug"
          value={slug}
          onChange={(e) => {
            const v = e.target.value;
            setSlug(v);
            setSlugManuallyEdited(v !== '');
          }}
          required
          className={inputClass}
        />
        <select
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
          className={inputClass}
        >
          <option value="">— Sem pai (topo)</option>
          {topLevel.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(Number(e.target.value))}
          className={inputClass}
          placeholder="Ordem"
        />
        <button type="submit" disabled={slugCollision} className={primaryBtn}>
          Criar
        </button>
      </form>
      {slugCollision ? (
        <p className="mb-4 text-sm text-admin-danger">
          Já existe uma categoria com o slug "{slug}".
        </p>
      ) : null}

      <ul className="overflow-hidden rounded-lg border border-admin-border bg-admin-surface">
        {topLevel.length === 0 ? (
          <li className="p-4 text-sm text-admin-muted">Sem categorias.</li>
        ) : null}
        {topLevel
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .flatMap((p) => [
            renderRow(p, 0),
            ...(childrenByParent.get(p.id) ?? []).map((child) => renderRow(child, 1)),
          ])}
      </ul>
    </section>
  );
}
